import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api from "@/api";
import Seo, { absoluteAsset, absoluteUrl, breadcrumbSchema, organizationSchema } from "@/components/Seo";

const coverSrc = (cover) => (
  cover && (cover.startsWith("http") || cover.startsWith("/"))
    ? cover
    : `${process.env.REACT_APP_BACKEND_URL}/api/files/${cover}`
);

const JournalPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <>
        <Seo
          title="Story Not Found | Chai Times Journal"
          description="This Chai Times journal story could not be found."
          path={`/journal/${slug}`}
          noindex
        />
        <div className="pt-44 pb-24 text-center" data-testid="journal-post-missing">
          <p className="font-mono text-sm text-charcoal/50">Story not found.</p>
          <Link to="/journal" className="text-terracotta font-mono text-xs uppercase tracking-[0.2em] mt-4 inline-block">Back to journal</Link>
        </div>
      </>
    );
  }
  if (!post) {
    return <div className="pt-44 pb-24 text-center font-mono text-sm text-charcoal/50" data-testid="journal-post-loading">Pouring…</div>;
  }

  return (
    <article data-testid="journal-post-page" className="pt-36 md:pt-44 pb-24">
      <Seo
        title={`${post.title} | Chai Times Journal`}
        description={post.excerpt || `Read ${post.title} on the Chai Times Journal.`}
        path={`/journal/${post.slug}`}
        image={post.cover}
        type="article"
        schema={[
          {
            "@type": "BlogPosting",
            "@id": absoluteUrl(`/journal/${post.slug}#article`),
            headline: post.title,
            description: post.excerpt,
            image: absoluteAsset(post.cover),
            datePublished: post.date,
            dateModified: post.date,
            author: { "@id": organizationSchema["@id"] },
            publisher: { "@id": organizationSchema["@id"] },
            mainEntityOfPage: absoluteUrl(`/journal/${post.slug}`),
            articleSection: post.tag,
            inLanguage: "en-IN",
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Journal", path: "/journal" },
            { name: post.title, path: `/journal/${post.slug}` },
          ]),
        ]}
      />
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Reveal>
          <Link to="/journal" data-testid="journal-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-terracotta hover:text-charcoal transition-colors">
            <ArrowLeft size={14} /> Journal
          </Link>
          <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.2em] text-terracotta mt-10">
            <span>{post.tag}</span><span className="text-charcoal/40">{post.date}</span>
          </div>
          <h1 className="font-serif font-black text-4xl md:text-6xl tracking-tight mt-4 leading-[1.02]">{post.title}</h1>
        </Reveal>
        <Reveal delay={0.1}>
          <img src={coverSrc(post.cover)} alt={post.title} className="rounded-2xl w-full aspect-[16/9] object-cover mt-10" />
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 space-y-6">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i} className={`text-charcoal/80 leading-relaxed text-lg ${i === 0 ? "dropcap" : ""}`}>{para}</p>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-14 border-t border-clayline pt-8 flex items-center justify-between">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">Chai Times — Bengaluru</p>
            <Link to="/menu" data-testid="journal-menu-cta" className="font-mono text-xs uppercase tracking-[0.2em] text-terracotta hover:text-charcoal transition-colors">
              Taste it yourself →
            </Link>
          </div>
        </Reveal>
      </div>
    </article>
  );
};

export default JournalPost;
